import React, { memo, useCallback } from 'react';

import Bar from '@/components/atomic/atoms/navigation/bar';
import HamburgerWrapper from '@/components/atomic/atoms/navigation/hamburger-wrapper';
import Link from '@/components/atomic/atoms/navigation/link';
import MenuItemText from '@/components/atomic/atoms/navigation/menu/text';
import type { HamburgerProps } from '@/components/atomic/molecules/navigation/hamburger/typings';
import withExposer from '@/hocs/with-exposer';
import useStore from '@/state';
import closeNavigationAction from '@/state/selectors/navigation/close-navigation';
import getDropdownId from '@/state/selectors/navigation/get-dropdown-id';
import getOpenState from '@/state/selectors/navigation/get-open-state';
import openNavigationAction from '@/state/selectors/navigation/open-navigation';
import { BasicComponentProps } from '@/typings/generic';

const excludeDropdowns = ['my-account', 'contact-and-help'];

const HamburgerComponent: React.FC<HamburgerProps & BasicComponentProps> = ({
	mobileBurgerAriaLabel = 'MENU',
	mobileBurgerMenuLabel = 'MENU',
	$exposerSuffix,
}) => {
	const openNavigation = useStore(openNavigationAction);
	const closeNavigation = useStore(closeNavigationAction);

	const open = useStore(getOpenState);
	const dropdownId = useStore(getDropdownId);
	const mobileOpen = excludeDropdowns.includes(dropdownId) ? false : open;

	const toggleNavigation = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			e.preventDefault();
			if (open) {
				closeNavigation();
			} else {
				openNavigation(['mobileRoot']);
			}
		},
		[closeNavigation, open, openNavigation],
	);

	return (
		<Link
			onClick={toggleNavigation}
			href='#'
			aria-label={mobileBurgerAriaLabel}
			aria-expanded={open}
			aria-haspopup={true}
			$exposerSuffix={$exposerSuffix}
		>
			<HamburgerWrapper
				$open={mobileOpen}
				$exposerSuffix={$exposerSuffix}
			>
				<Bar
					$position='top'
					$open={mobileOpen}
					$exposerSuffix={`${$exposerSuffix}_top`}
				/>
				<Bar
					$position='middle'
					$open={mobileOpen}
					$exposerSuffix={`${$exposerSuffix}_middle`}
				/>
				<Bar
					$position='bottom'
					$open={mobileOpen}
					$exposerSuffix={`${$exposerSuffix}_bottom`}
				/>
			</HamburgerWrapper>
			{mobileBurgerMenuLabel ? (
				<MenuItemText $exposerSuffix={$exposerSuffix}>
					{mobileBurgerMenuLabel}
				</MenuItemText>
			) : null}
		</Link>
	);
};

const Hamburger = memo(withExposer(HamburgerComponent, 'hamburger'));

export default Hamburger;
